/**
 * In-page PayPal checkout modal (sc-6943).
 *
 * Replaces the full-page redirect to PayPal with PayPal's own in-page overlay:
 * the cabinet stays loaded, the buyer approves in the SDK's popup/overlay, and
 * on approval we finalize with the SAME server calls the redirect-return path
 * uses (captureOrder for a day pass; entitlement poll for both).
 *
 *   day pass (Orders v2)   → createOrder → onApprove → captureOrder → poll
 *   week/month (Subscription) → createSubscription → onApprove → poll (webhook grants)
 *
 * The API is unchanged: /billing/checkout already returns `ref` (the order or
 * subscription id) — the SDK callbacks just consume `ref` instead of the
 * `approveUrl` the redirect path used.
 *
 * If the SDK can't load, callers fall back to the redirect flow — this never
 * traps the buyer with no way to pay.
 */

import type { PassOption } from '@arcraz/arcadia-types';
import { captureOrder, startCheckout } from '../api/client.js';
import { waitForEntitlement } from './return.js';
import { loadPayPalSdk, type PayPalNamespace } from './paypal-sdk.js';

export interface CheckoutModalResult {
  /** 'entitled' → paid & pass active (relaunch); 'cancelled' → buyer bailed;
   *  'unavailable' → SDK couldn't load (caller should redirect-fallback);
   *  'error' → something failed after approval. */
  status: 'entitled' | 'cancelled' | 'unavailable' | 'error';
}

const RETURN_PATH = '/billing/return';
const CANCEL_PATH = '/billing/cancel';

/**
 * Open the in-page checkout for `pass`. Resolves when the buyer completes,
 * cancels, or the SDK is unavailable. Never rejects.
 */
export function openCheckoutModal(pass: PassOption): Promise<CheckoutModalResult> {
  const isSub = pass.kind !== 'day_pass';
  const intent = isSub ? 'subscription' : 'capture';

  return new Promise<CheckoutModalResult>((resolve) => {
    let settled = false;
    const done = (status: CheckoutModalResult['status']): void => {
      if (settled) return;
      settled = true;
      overlay.remove();
      resolve({ status });
    };

    // --- modal scaffold -----------------------------------------------------
    const overlay = document.createElement('div');
    overlay.className = 'checkout-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', `Buy the ${pass.label}`);

    const card = document.createElement('div');
    card.className = 'checkout-modal__card';

    // Header — title, big price, cadence, close.
    const header = document.createElement('div');
    header.className = 'checkout-modal__header';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'checkout-modal__close';
    close.setAttribute('aria-label', 'Close checkout');
    close.textContent = '✕';
    close.addEventListener('click', () => done('cancelled'));

    const title = document.createElement('h2');
    title.className = 'checkout-modal__title';
    title.textContent = pass.label;

    const price = document.createElement('div');
    price.className = 'checkout-modal__price';
    if (pass.listPriceUsd) {
      const was = document.createElement('span');
      was.className = 'checkout-modal__price-was';
      was.textContent = `$${pass.listPriceUsd}`;
      price.append(was);
    }
    const priceAmt = document.createElement('span');
    priceAmt.className = 'checkout-modal__price-amt';
    priceAmt.textContent = `$${pass.priceUsd}`;
    const priceCad = document.createElement('span');
    priceCad.className = 'checkout-modal__price-cadence';
    priceCad.textContent = pass.cadence;
    price.append(priceAmt, priceCad);
    header.append(close, title, price);

    // Body — the light "payment surface" that holds PayPal's buttons.
    const body = document.createElement('div');
    body.className = 'checkout-modal__body';

    const surface = document.createElement('div');
    surface.className = 'checkout-modal__surface';
    const buttons = document.createElement('div');
    buttons.className = 'checkout-modal__buttons';
    const spinner = document.createElement('div');
    spinner.className = 'checkout-modal__spinner';
    spinner.textContent = 'Loading secure checkout…';
    surface.append(spinner, buttons);

    const secure = document.createElement('p');
    secure.className = 'checkout-modal__secure';
    secure.textContent = '🔒 Secure checkout · encrypted by PayPal';

    const status = document.createElement('p');
    status.className = 'checkout-modal__status';

    body.append(surface, secure, status);
    card.append(header, body);
    overlay.appendChild(card);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) done('cancelled'); });
    document.body.appendChild(overlay);

    // Return/cancel URLs still passed to the API (PayPal wants them even in the
    // SDK flow); the SDK resolves in-page, so we never actually navigate there.
    const origin = window.location.origin;
    const returnUrl = `${origin}${RETURN_PATH}?kind=${encodeURIComponent(pass.kind)}`;
    const cancelUrl = `${origin}${CANCEL_PATH}`;

    void loadPayPalSdk(intent).then((paypal: PayPalNamespace | null) => {
      if (settled) return;
      if (!paypal) { done('unavailable'); return; } // caller → redirect fallback

      spinner.style.display = 'none'; // buttons render below
      status.textContent = '';

      const common = {
        onCancel: () => { status.textContent = 'Checkout cancelled.'; done('cancelled'); },
        onError: (err: unknown) => {
          status.textContent = 'Something went wrong. Please try again.';
          console.warn('[arcadia84] paypal button error', { err });
          // Leave the modal open briefly so the buyer sees the message.
          window.setTimeout(() => done('error'), 1200);
        },
        style: { layout: 'vertical' as const, color: 'gold' as const, shape: 'pill' as const, label: 'pay' as const },
      };

      const config = isSub
        ? {
            ...common,
            // Subscription: /billing/checkout mints the subscription; ref = its id.
            createSubscription: () =>
              startCheckout(pass.kind, returnUrl, cancelUrl).then((r) => r.ref),
            onApprove: async () => {
              status.textContent = 'Confirming your pass…';
              // Subs are granted by the webhook — just poll the wallet.
              done((await waitForEntitlement()) ? 'entitled' : 'error');
            },
          }
        : {
            ...common,
            // Day pass: /billing/checkout mints the Order; ref = its id.
            createOrder: () =>
              startCheckout(pass.kind, returnUrl, cancelUrl).then((r) => r.ref),
            onApprove: async (data: { orderID?: string }) => {
              status.textContent = 'Processing payment…';
              try {
                // Capture to collect the money; tolerate an already-captured
                // error (webhook is the grant source of truth), then poll.
                if (data.orderID) await captureOrder(data.orderID);
              } catch (err) {
                console.warn('[arcadia84] capture error (webhook still authoritative)', { err });
              }
              done((await waitForEntitlement()) ? 'entitled' : 'error');
            },
          };

      try {
        void paypal.Buttons(config).render(buttons);
      } catch (err) {
        console.warn('[arcadia84] paypal render failed', { err });
        done('unavailable');
      }
    });
  });
}
